/**
 * @file tableGroups.ts
 * @brief Helpers for collapsible DataTable groups (company / user / status).
 *
 * @author DES
 * @date 08-07-2026
 */

import { getInitials } from "@/shared/utils/formatters";

export const PERSONAL_GROUP_KEY = "__personal__";
const PERSONAL_KEY = PERSONAL_GROUP_KEY;
const NO_COMPANY_KEY = "__no_company__";

export function companyGroupKey(
  companyId?: string | null,
  companyName?: string | null,
): string {
  if (companyId?.trim()) return companyId.trim();
  if (companyName?.trim()) return `name:${companyName.trim().toLowerCase()}`;
  return PERSONAL_KEY;
}

export function companyGroupLabel(companyName?: string | null): string {
  return companyName?.trim() || "Personal";
}

export function companyGroupMeta(companyName?: string | null) {
  const label = companyGroupLabel(companyName);
  const initials =
    label === "Personal"
      ? "P"
      : label
          .split(/\s+/)
          .filter(Boolean)
          .map((part) => part[0] ?? "")
          .join("")
          .slice(0, 2)
          .toUpperCase() || "CO";

  return { label, avatarInitials: initials };
}

export function userCompanyGroupKey(row: {
  company_id?: string | null;
  company_name?: string | null;
  company?: string | null;
}): string {
  if (row.company_id?.trim()) return row.company_id.trim();
  const name = row.company_name?.trim() || row.company?.trim();
  if (name) return `name:${name.toLowerCase()}`;
  return NO_COMPANY_KEY;
}

export function userCompanyGroupLabel(row: {
  company_name?: string | null;
  company?: string | null;
}): string {
  return row.company_name?.trim() || row.company?.trim() || "No company";
}

export function userCompanyGroupMeta(row: {
  company_name?: string | null;
  company?: string | null;
}) {
  const label = userCompanyGroupLabel(row);
  return {
    label,
    avatarInitials:
      label === "No company"
        ? "—"
        : label
            .split(/\s+/)
            .filter(Boolean)
            .map((part) => part[0] ?? "")
            .join("")
            .slice(0, 2)
            .toUpperCase() || "CO",
  };
}

export function feedbackUserGroupKey(
  userId?: string | null,
  email?: string | null,
): string {
  if (userId?.trim()) return userId.trim();
  if (email?.trim()) return email.trim().toLowerCase();
  return "__unknown_user__";
}

export function feedbackUserGroupMeta(
  first?: string | null,
  last?: string | null,
  email?: string | null,
) {
  const name = [first, last].filter(Boolean).join(" ").trim();
  const label = name || email || "Unknown user";
  return {
    label,
    avatarInitials: getInitials(
      first ?? undefined,
      last ?? undefined,
      email ?? undefined,
    ),
  };
}

export function statusGroupKey(status: string): string {
  return status || "unknown";
}

export function statusGroupLabel(status: string): string {
  if (!status) return "Unknown";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function statusGroupMeta(status: string) {
  const label = statusGroupLabel(status);
  return {
    label,
    avatarInitials: label.slice(0, 2).toUpperCase(),
  };
}
